/**
 * The sound of the throw. **four voices, not a soundbed**.
 *
 * Ported from Armarium (`apps/web/src/lib/dice3d/dice-audio.ts`). Each voice
 * says exactly one thing:
 *
 * 1. **The cushion** (`bank`). Wood on wood. The die slams into the rim.
 * 2. **The wheel** (`rollStart`). While it flies, a wheel runs. Nothing else.
 * 3. **The fall** (`rattle`). When it stops, a die rattles.
 * 4. **The verdict** (`reveal`). And then a number stands there.
 *
 * ## Why wood is made out of a sheet of tin
 *
 * The Kenney sample pack has glass (2.8 kHz), plate (3.2 kHz), metal (3.6 kHz),
 * bell (2.6 kHz). And one single dull piece, `impactsoft` at 1.6 kHz.
 * Everything else rings.
 *
 * A wooden knock isn't merely "darker": it is an **attack without a tail**.
 * What makes it is the belly around three hundred hertz and the fact that after
 * a tenth of a second nothing is left. Both can be pulled out of a ringing
 * recording. Low-pass against the ring, a peaking filter at 300 Hz for the
 * belly, and a short envelope that cuts the reverb off before it starts. That
 * is the difference between a plate and a board, and it lives in `voice`.
 *
 * The `AudioContext` is created on the first sound only. If the browser refuses
 * audio without a user gesture it stays silent. The throw works without ears.
 */

import { getLocalApiOrigin } from '../../localApi';
import { getEffectiveSfxVolume } from '../../arena/soundSettings';

/**
 * The recordings, by prefix. Listed rather than globbed: Bun's bundler has no
 * `import.meta.glob`, and these are served as static files from
 * `/game-assets/dice-sfx/` anyway.
 */
const POOLS = {
  /** The die falling: a real recording, and the only one left as it is. */
  throw: ['dice-throw-1', 'dice-throw-2', 'dice-throw-3'],
  /** The dullest piece in the pack. The raw material for everything wooden. */
  soft: ['impactsoft-heavy-000'],
  /** Rings, but is varied: filtered it gives the spread of the cushion. */
  plate: ['impactplate-light-000', 'impactplate-light-001', 'impactplate-light-002'],
  metal: ['impactmetal-light-000', 'impactmetal-light-001', 'impactmetal-light-002'],
  glass: [
    'impactglass-light-000', 'impactglass-light-001', 'impactglass-light-002',
    'impactglass-light-003', 'impactglass-light-004',
  ],
  /** The only long piece (a second and a half). Only the natural 20 reaches for it. */
  bell: ['impactbell-heavy-000', 'impactbell-heavy-001'],
};

const url = (name) => `${getLocalApiOrigin()}/game-assets/dice-sfx/${name}.mp3`;

/** The resting level the fade of each throw ramps up to. */
const MASTER = 0.5;

let ctx = null;
let master = null;
let lastBank = 0;
/**
 * The teeth of the running wheel, scheduled in advance. A second throw
 * overtaking a first has to be able to clear them, or two wheels run on top of
 * each other.
 */
let wheel = [];
/** Serial number of the throw. It discards anything belonging to an old one. */
let throwId = 0;
const decoded = new Map();
let primed = null;
/** Whether `prime` is through. Successfully or not, but done either way. */
let ready = false;

function audio() {
  if (typeof window === 'undefined' || typeof AudioContext === 'undefined') return null;
  if (ctx === null) {
    try {
      ctx = new AudioContext();
    } catch {
      return null;
    }
    master = ctx.createGain();
    master.gain.value = MASTER;
    master.connect(ctx.destination);
  }
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

/**
 * Bytes become buffers. Once, as soon as there is an audio clock. The promise
 * is kept when every recording has either landed or failed, so nobody waiting
 * on it waits in vain.
 */
function prime(c) {
  if (primed !== null) return primed;
  const names = Object.values(POOLS).flat();
  primed = Promise.all(names.map((name) => fetch(url(name))
    .then((r) => r.arrayBuffer())
    .then((raw) => c.decodeAudioData(raw))
    .then((buffer) => { decoded.set(name, buffer); })
    .catch(() => {}))).then(() => { ready = true; });
  return primed;
}

/**
 * Preload the recordings. Called when the dice modal mounts: fetching them at
 * the first sound would leave the first throw of a session silent, because
 * decoding takes a moment after that.
 */
export function preloadDiceAudio() {
  const c = audio();
  if (c !== null) prime(c);
}

/**
 * Do something once the recordings are there. **now**, if they already are.
 * For the sounds that are still right a moment later: a rattle a tenth of a
 * second late beats no rattle at all.
 */
function whenReady(fn) {
  if (ready) return true;
  const c = audio();
  if (c !== null) prime(c).then(fn);
  return false;
}

/**
 * One recording, randomised in pitch, strength, place and **shape**.
 *
 * Recordings not yet decoded stay silent. Better a missing knock at the start
 * than a late one in the middle of the throw.
 */
function voice({ names, rate, gain, when = 0, pan = 0, lowpass, highpass, body, decay }) {
  const c = audio();
  if (c === null || master === null || names.length === 0) return null;
  const sfx = getEffectiveSfxVolume();
  if (sfx <= 0) return null;
  const buffer = decoded.get(names[Math.floor(Math.random() * names.length)]);
  if (buffer === undefined) return null;

  const at = when === 0 ? c.currentTime : when;
  const source = c.createBufferSource();
  source.buffer = buffer;
  source.playbackRate.value = rate;

  const level = gain * sfx;
  const env = c.createGain();
  env.gain.setValueAtTime(level, at);
  if (decay !== undefined) {
    // Exponential with a hard cut at the end: a tail running all the way to
    // zero is no longer a tail, it is a remainder.
    env.gain.exponentialRampToValueAtTime(level * 0.0016, at + decay);
    env.gain.setValueAtTime(0, at + decay + 0.005);
  }

  let tail = source.connect(env);
  if (highpass !== undefined) {
    const filter = c.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = highpass;
    filter.Q.value = 0.7;
    tail = tail.connect(filter);
  }
  if (lowpass !== undefined) {
    const filter = c.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = lowpass;
    filter.Q.value = 0.7;
    tail = tail.connect(filter);
  }
  if (body !== undefined) {
    const filter = c.createBiquadFilter();
    filter.type = 'peaking';
    filter.frequency.value = body[0];
    filter.gain.value = body[1];
    filter.Q.value = 1.1;
    tail = tail.connect(filter);
  }
  if (pan !== 0 && typeof c.createStereoPanner === 'function') {
    const panner = c.createStereoPanner();
    panner.pan.value = pan < -1 ? -1 : pan > 1 ? 1 : pan;
    tail = tail.connect(panner);
  }
  tail.connect(master);
  source.start(at);
  if (decay !== undefined) source.stop(at + decay + 0.02);
  return source;
}

const jitter = (spread) => 1 + (Math.random() - 0.5) * spread;

/**
 * **The cushion: wood on wood.**
 *
 * Two voices on top of each other, because an attack is two things. The strike
 * and what the board does afterwards. Below, the dull piece, pitched up and
 * trimmed at the bottom: what carries here is the plate mode around a kilohertz,
 * not the drum below it. Above, a breath of plate, hard-cut: that is the
 * fingernail in the attack, not the tone. A thin plate does not reverberate, so
 * it is over after forty milliseconds.
 *
 * Barely throttled. Wall hits are rare and counted. The lockout is only long
 * enough that two dice striking on the same frame don't merge into one knock at
 * double the volume.
 */
export function bank(intensity, pan = 0) {
  const c = audio();
  if (c === null) return;
  if (c.currentTime - lastBank < 0.03) return;
  lastBank = c.currentTime;
  const punch = Math.min(1, Math.max(0.25, intensity));

  voice({
    names: POOLS.soft,
    rate: (2.6 - punch * 0.4) * jitter(0.13),
    gain: (0.36 + punch * 0.34) * jitter(0.18),
    pan,
    highpass: 340,
    lowpass: 4200 + punch * 1000,
    body: [980 - punch * 160, 5.5],
    decay: 0.05 + punch * 0.03,
  });
  voice({
    names: POOLS.plate,
    rate: 2.4 * jitter(0.2),
    gain: (0.15 + punch * 0.24) * jitter(0.3),
    pan,
    highpass: 1400,
    lowpass: 7000,
    decay: 0.018,
  });
}

/**
 * **The wheel.**
 *
 * While the die flies a wheel runs. Fast at first, visibly slowing toward the
 * end, like a wheel of fortune looking for its stop.
 *
 * The clicks lie **on the die's own curve**, not on one of their own. The throw
 * turns as `1 - (1-s)^ease`; a wheel clicks once per tooth, i.e. once per equal
 * piece of rotation. Solving for time gives when the k-th tooth passes. So the
 * wheel slows down exactly when the die does.
 *
 * Scheduled **in advance**, not ticked: `setInterval` runs on the frame clock
 * and wobbles with load; the audio clock does not. A wheel that audibly wows is
 * worse than none.
 */
const WHEEL_TEETH = 30;
/**
 * The run-out of the wheel. Flatter than the die's: at the die's own ease the
 * last tooth fell at 79% of the trip and the die then spun on in silence for a
 * good quarter second. A wheel that stops before it stands sounds broken.
 */
const WHEEL_EASE = 1.35;
/** From here the fall takes over; a tooth after this would land behind the result. */
const WHEEL_UNTIL = 0.96;
/** How loud the bright hook sits over the dull tooth. Twenty-four dB under it. */
const WHEEL_SNAP = 0.06;
/** How far above the tooth the hook sits. A ratio, so it rises and falls *with* it. */
const WHEEL_SNAP_RATIO = 1.3;

export function rollStart(expectedSeconds = 1.75) {
  const c = audio();
  if (c === null || master === null) return;
  rollEnd();
  const mine = ++throwId;
  const now = c.currentTime;

  // **The fade.** Without it the throw begins on the edge of the first sample.
  // a click no die makes.
  master.gain.cancelScheduledValues(now);
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(MASTER, now + 0.06);

  // **One roll of the dice for both layers.** Pitch and strength are drawn
  // *once* here and then shared. Drawn separately, their ratio wobbles from
  // tooth to tooth and the ear follows the independent part as its own source.
  // which is why the two used to sound beside each other instead of on top.
  const teeth = [];
  for (let k = 1; k <= WHEEL_TEETH; k++) {
    const s = 1 - Math.pow(1 - k / WHEEL_TEETH, 1 / WHEEL_EASE);
    if (s > WHEEL_UNTIL) break;
    teeth.push({
      when: now + s * expectedSeconds,
      swing: (2.9 + Math.random() * 0.5) * jitter(0.06),
      // The wheel gets quieter as it gets slower: a run-out wheel does not just
      // click more rarely, it clicks duller.
      strength: (0.3 - 0.16 * (k / WHEEL_TEETH)) * jitter(0.22),
    });
  }

  const lay = () => {
    // A wheel belonging to a past throw stays where it is.
    if (mine !== throwId) return;
    for (const { when, swing, strength } of teeth) {
      // A tooth whose time has passed is dropped: a late click is worse than a
      // missing one.
      if (when <= c.currentTime) continue;

      // **Below, the tooth.** The dull piece, pitched up and trimmed: the wood
      // the pawl lifts.
      const tooth = voice({
        names: POOLS.soft,
        rate: swing,
        gain: strength,
        when,
        lowpass: 2600,
        body: [900, 4],
        decay: 0.028,
      });
      if (tooth !== null) wheel.push(tooth);

      // **Above, the hook.** Not a second knock but the sheen on the first:
      // same pitch and strength as the tooth, only set higher and turned right
      // down. Cut off at exactly where the tooth's low-pass ends. A gap
      // between the two would be heard as two things.
      const hook = voice({
        names: POOLS.glass,
        rate: swing * WHEEL_SNAP_RATIO,
        gain: strength * WHEEL_SNAP,
        when,
        highpass: 2600,
        decay: 0.011,
      });
      if (hook !== null) wheel.push(hook);
    }
  };

  // **The wheel is laid as soon as it can be, not merely when convenient.** All
  // teeth are created in *one* instant, and if that was the first instant of a
  // session no recording had been decoded yet: every tooth dropped out and the
  // first throw ran silent.
  if (whenReady(lay)) lay();
}

/**
 * End of throw: clear the wheel. A regular end and an abort do the same thing
 * here. Teeth still in the future belong to a throw that is over.
 */
export function rollEnd() {
  // The not-yet-laid counts too: if a wheel is waiting on the recordings, it
  // must not go off afterwards.
  throwId += 1;
  for (const tooth of wheel) {
    try {
      tooth.stop();
    } catch {
      // Already finished or never started. Both are fine.
    }
  }
  wheel = [];
}

/**
 * **The fall.** When the die stops, it rattles. A real recording, barely
 * re-pitched. The only sound in the whole throw that was not rebuilt: what
 * sounds like a die does not need to be made into one.
 *
 * **The second die is quieter than the first.** Whoever falls shortly after
 * another falls quieter and a shade higher, like a smaller body following it.
 */
const RATTLE_WINDOW = 0.45;
let lastRattle = -1;
let rattleRun = 0;

export function rattle() {
  const c = audio();
  if (c === null) return;
  // The fall is the sound the throw hangs on. If the recording is not ready it
  // is handed in late rather than dropped.
  if (!whenReady(rattle)) return;
  rattleRun = c.currentTime - lastRattle < RATTLE_WINDOW ? rattleRun + 1 : 0;
  lastRattle = c.currentTime;
  const later = Math.min(4, rattleRun);
  voice({
    names: POOLS.throw,
    rate: (1.02 + later * 0.06) * jitter(0.1),
    gain: 0.72 * Math.pow(0.62, later),
  });
}

/**
 * **The chime of the natural 20: a triad out of wood.**
 *
 * A marimba bar is two things at once. A wooden strike and a tube resonating
 * beneath it. It is built that way here: `soft`, pitched up and shut after
 * fifty milliseconds, is the mallet on the wood; the bell beneath, pitched down
 * and stripped of its metal, is the tube.
 *
 * The tones are in **just intonation** (1 : 5/4 : 3/2 : 2). Pure ratios rather
 * than the keyboard's, because the partials of a percussion recording do not
 * sit on a grid anyway: what makes for warmth here are beats that aren't.
 *
 * The root is given as a **frequency**, not a playback rate, and the rate is
 * derived from it via the bell recording's own measured pitch. And because a
 * transposition alone would *bend* the sound. The low-pass would stay put
 * while the tone wandered away underneath it. Filter and body hang off the
 * root instead of fixed hertz values.
 */
const CHIME = [1, 5 / 4, 3 / 2, 2];
/** The root of the chime in hertz. The one number you want to turn. */
const CHIME_ROOT = 300;
/** The bell recording's own pitch at rate 1. Measured, not guessed. */
const BELL_TONE = 380;
/** Where the low-pass sits, in multiples of the root. Lower means rounder. */
const CHIME_ROLLOFF = 10;
/** Where the body resonates. On a wooden bar above the tone, not on it. */
const CHIME_BODY = 2.75;
/** Spacing of the tones. Tight enough that it stays a sound, not a melody. */
const CHIME_STEP = 0.07;

/**
 * **The verdict: every throw gets its sound.**
 *
 * The fall says the die is *lying there*; the verdict says a number *stands*.
 * Those are two things.
 *
 * - **Natural 20**. The chime. Always the same, so it is recognised.
 * - **The ordinary throw**. Metal or plate, short and darkly trimmed. A sound,
 *   not a fanfare.
 * - **Natural 1**. The dull piece, low and filtered away. Muted, because
 *   nothing should ring on.
 *
 * For the ordinary throw and the natural 1 the pitch is drawn **continuously**,
 * never picked off a scale: as soon as two throws stand in a ratio the ear
 * knows as an interval, the roll sounds like music instead of an object. The
 * natural 20 is the exception for the same reason. It is not an object
 * falling, it is an announcement, and an announcement may hit a note. It must
 * even hit the same one every time.
 */
export function reveal(crit) {
  const c = audio();
  if (c === null) return;
  // Like the fall: a blink late beats not at all. The chime works off
  // `c.currentTime`, so the whole verdict is retried, not just its voices.
  if (!whenReady(() => reveal(crit))) return;
  const rand = Math.random();

  if (crit === 'high') {
    const now = c.currentTime;
    CHIME.forEach((tone, i) => {
      const hz = CHIME_ROOT * tone;
      const when = now + i * CHIME_STEP;
      // The mallet: wood on wood, over before the tone stands. It rises with
      // the bar but not along with it. A mallet does not change register just
      // because the bar gets shorter.
      voice({
        names: POOLS.soft,
        rate: 2.2 * Math.sqrt(tone),
        gain: 0.24,
        when,
        lowpass: 2600,
        decay: 0.05,
      });
      // The tube: the bell, re-tuned with its metal capped. The body sits
      // **not** on the root but a good octave and a half above. Where a wooden
      // bar's body resonates and the tone does not. The upper tones stand
      // quieter so the octave crowns rather than stings.
      voice({
        names: POOLS.bell,
        rate: hz / BELL_TONE,
        gain: 0.3 - i * 0.035,
        when,
        lowpass: hz * CHIME_ROLLOFF,
        body: [hz * CHIME_BODY, 6],
        decay: 0.5 + i * 0.22,
      });
    });
    return;
  }

  if (crit === 'low') {
    voice({
      names: POOLS.soft,
      rate: 0.5 + rand * 0.22,
      gain: 0.72 + Math.random() * 0.12,
      lowpass: 600 + Math.random() * 340,
      body: [250, 5],
      decay: 0.3 + Math.random() * 0.2,
    });
    return;
  }

  voice({
    names: Math.random() < 0.5 ? POOLS.metal : POOLS.plate,
    rate: 0.7 + rand * 0.46,
    gain: 0.4 + Math.random() * 0.14,
    lowpass: 2100 + Math.random() * 2300,
    decay: 0.26 + Math.random() * 0.24,
  });
}
